import { HttpClient } from '@angular/common/http';
import { inject, Injectable, signal } from '@angular/core';
import { ENVIRONMENT } from '@ld.consumer-portal/models';
import { firstValueFrom } from 'rxjs';
import { LoggingService } from './logging.service';

export const ENTRA_LOGIN_ACCELERATION_BLOCKLIST_URL = '/assets/config/entra-login-acceleration-blocklist.json';

/** Used when `appSettings.entraEmailReentryNoticeDurationMs` is not configured. */
export const DEFAULT_EMAIL_REENTRY_NOTICE_DURATION_MS = 10000;

/**
 * Decides whether Entra login acceleration (`login_hint`) may be used for a given email.
 *
 * ## Why this exists
 *
 * We collect the user's email on our own login page and forward it to Entra as `login_hint`,
 * so the user does not have to type it a second time. This is known as login acceleration.
 *
 * Per Microsoft, `login_hint` does more than pre-fill the username: Entra resolves the address
 * to its home tenant first. When the email belongs to a domain that has its own Microsoft Entra
 * home tenant (typically government, military, education and federated corporate domains), the
 * user is sent to *that* tenant's password step instead of our CIAM sign-in page. From the
 * user's perspective the portal "redirects to their employer's site" and their consumer portal
 * credentials are not accepted — see CP-793, where @us.army.mil users landed on a military
 * sign-in page. `domain_hint` alone does not reliably override this home-realm resolution.
 *
 * ## The remedy
 *
 * Only the domains listed in the blocklist asset opt out of login acceleration. For those
 * users the `login_hint` is withheld, Entra has nothing to resolve, and its own sign-in page is
 * rendered. The accepted trade-off is that those users type their email twice: once on our
 * login page and once on the Entra page. Every other domain keeps the accelerated single-entry
 * experience.
 *
 * ## Matching rules
 *
 * Patterns are matched case-insensitively against the email's domain and support a leading
 * wildcard: `*.gov` matches `gov`, `ca.gov` and `state.ca.gov`, while the bare `ca.gov` matches
 * only `ca.gov` and not its subdomains.
 *
 * The list is a deployed asset rather than a compiled constant, so a newly discovered domain
 * can be published without rebuilding and re-releasing the app.
 *
 * ## Usage
 *
 * Callers use {@link resolveLoginHint} immediately before issuing the Entra authorize request.
 * It both decides the hint and, for a suppressed domain, raises the email re-entry notice for
 * the configured duration so the borrower is warned while the hand-off is imminent.
 */
@Injectable({ providedIn: 'root' })
export class EntraLoginAccelerationEvaluatorService {
  private readonly http = inject(HttpClient);
  private readonly loggingService = inject(LoggingService);
  private readonly env = inject(ENVIRONMENT);

  private blockedDomains: Promise<readonly string[]> | null = null;

  private readonly noticeVisible = signal(false);
  private readonly noticeCountdown = signal(0);
  private noticeResolver: (() => void) | null = null;
  private noticeInterval: ReturnType<typeof setInterval> | null = null;

  /** Whether the login page should be rendering the email re-entry notice. */
  readonly emailReentryNoticeVisible = this.noticeVisible.asReadonly();

  /**
   * Seconds left before the borrower is handed off automatically. Counts down to 0, which the
   * notice renders as its "redirecting now" state.
   */
  readonly emailReentryCountdownSeconds = this.noticeCountdown.asReadonly();

  /** Configured via `appSettings.entraEmailReentryNoticeDurationMs`. */
  get emailReentryNoticeDurationMs(): number {
    return this.env.appSettings.entraEmailReentryNoticeDurationMs ?? DEFAULT_EMAIL_REENTRY_NOTICE_DURATION_MS;
  }

  /**
   * Returns the `login_hint` to attach to the Entra authorize request, or `null` when the
   * domain is blocklisted and the hint must be withheld.
   *
   * Call this immediately before the authorize request: for a suppressed domain it shows the
   * email re-entry notice and only resolves once the borrower acknowledges it or the countdown
   * runs out, so the warning is read right as they are handed off rather than seconds earlier.
   */
  async resolveLoginHint(email: string): Promise<string | null> {
    if (await this.shouldSendLoginHint(email)) {
      return email;
    }

    await this.awaitEmailReentryAcknowledgement();
    return null;
  }

  /** Lets the borrower skip the remaining countdown and continue to Entra straight away. */
  confirmEmailReentryNotice(): void {
    this.loggingService.logInfo('[EntraLoginAcceleration] Email re-entry notice acknowledged by the borrower');
    this.settleEmailReentryNotice();
  }

  /**
   * Tears the notice down. Required when the hand-off fails, and on native once the system
   * browser closes and the app is visible again.
   */
  hideEmailReentryNotice(): void {
    this.settleEmailReentryNotice();
    this.noticeVisible.set(false);
  }

  /**
   * Shows the notice and resolves on whichever comes first: the borrower pressing OK, or the
   * countdown reaching zero. The countdown is driven in whole seconds because it is rendered on
   * the button, so a sub-second duration still holds for one tick.
   */
  private awaitEmailReentryAcknowledgement(): Promise<void> {
    this.noticeCountdown.set(Math.max(1, Math.ceil(this.emailReentryNoticeDurationMs / 1000)));
    this.noticeVisible.set(true);

    return new Promise<void>((resolve) => {
      this.noticeResolver = resolve;
      this.noticeInterval = setInterval(() => {
        const remaining = this.noticeCountdown() - 1;
        this.noticeCountdown.set(Math.max(0, remaining));
        if (remaining <= 0) {
          this.settleEmailReentryNotice();
        }
      }, 1000);
    });
  }

  /**
   * Stops the countdown and releases `resolveLoginHint`.
   *
   * The remaining seconds are left exactly as they were: pressing OK must not rewind or zero
   * the timer, it only stops it. The notice also deliberately stays visible, because the
   * authorize request follows immediately and hiding it here would flash the login form.
   */
  private settleEmailReentryNotice(): void {
    if (this.noticeInterval !== null) {
      clearInterval(this.noticeInterval);
      this.noticeInterval = null;
    }

    const resolve = this.noticeResolver;
    this.noticeResolver = null;
    resolve?.();
  }

  /**
   * Resolves to `false` when the email's domain is blocklisted and `login_hint` must be
   * withheld from the Entra authorize request (the user re-enters their email on the Entra
   * page), or `true` to proceed with the default accelerated behavior.
   */
  async shouldSendLoginHint(email: string): Promise<boolean> {
    const domain = extractDomain(email);
    if (!domain) {
      return true;
    }

    const patterns = await this.loadBlockedDomains();
    const matched = patterns.find((pattern) => matchesDomain(domain, pattern));
    if (!matched) {
      return true;
    }

    this.loggingService.logInfo(
      `[EntraLoginAcceleration] login_hint suppressed | domain=${domain} | pattern=${matched}`,
    );
    return false;
  }

  /** Loads the blocklist asset once and caches it for the lifetime of the app. */
  async loadBlockedDomains(): Promise<readonly string[]> {
    this.blockedDomains ??= this.fetchBlockedDomains();
    return this.blockedDomains;
  }

  private async fetchBlockedDomains(): Promise<readonly string[]> {
    try {
      const domains = await firstValueFrom(this.http.get<string[]>(ENTRA_LOGIN_ACCELERATION_BLOCKLIST_URL));
      return (domains ?? []).map((domain) => domain.trim().toLowerCase()).filter((domain) => domain.length > 0);
    } catch (error) {
      // Fail open: an unreachable blocklist must not stop users from signing in. Drop the
      // cached rejection so the next login attempt retries the fetch.
      this.blockedDomains = null;
      this.loggingService.logException(
        error,
        `[EntraLoginAcceleration] failed to load ${ENTRA_LOGIN_ACCELERATION_BLOCKLIST_URL} — sending login_hint`,
      );
      return [];
    }
  }
}

function extractDomain(email: string): string {
  const separator = email?.lastIndexOf('@') ?? -1;
  if (separator < 0) {
    return '';
  }
  return email
    .slice(separator + 1)
    .trim()
    .toLowerCase()
    .replace(/\.$/, '');
}

function matchesDomain(domain: string, pattern: string): boolean {
  if (pattern.startsWith('*.') || pattern.startsWith('.')) {
    const suffix = pattern.replace(/^\*?\./, '');
    return domain === suffix || domain.endsWith(`.${suffix}`);
  }
  return domain === pattern;
}
